const KEY = 'STATIC.PAGES.FRAUD.CUSTOM_RULES';

export const TRANSLATION_KEY = KEY;
export const FAQ_LINK =
    'https://support.singular.net/hc/en-us/articles/360003141571-How-to-Set-Up-Singular-Fraud-Protection#Set_Up_Custom_Rules_Optional';

export const MISSING_OPERATOR = { value: 'missing', labelKey: `${KEY}.OPERATORS.ABSENT` };
const GREATER_THAN_OPERATOR = { value: 'gt', labelKey: `${KEY}.OPERATORS.GREATER_THAN` };
const LESS_THAN_OPERATOR = { value: 'lt', labelKey: `${KEY}.OPERATORS.LESS_THAN` };

export const IN_NOT_IN = [
    { value: 'in', labelKey: `${KEY}.OPERATORS.INCLUDE` },
    { value: 'not_in', labelKey: `${KEY}.OPERATORS.EXCLUDE` },
];
export const LESS_THAN_GREATER_THAN = [LESS_THAN_OPERATOR, GREATER_THAN_OPERATOR];

const ORDER_OPERATORS_AND_MISSING_VALUE = [GREATER_THAN_OPERATOR, LESS_THAN_OPERATOR];

const ATTRIBUTION_METHODS = [
    { value: 'fingerprinting', labelKey: `${KEY}.OPERATORS.FINGERPRINTING` },
    { value: 'strong_id', labelKey: `${KEY}.OPERATORS.STRONG_ID` },
];

const OS_OPTIONS = [
    { value: 'Android', labelKey: `${KEY}.OPERATORS.ANDROID` },
    { value: 'iOS', labelKey: `${KEY}.OPERATORS.IOS` },
];

const TOUCHPOINT_TYPE_OPTIONS = [
    { value: 'click', labelKey: `${KEY}.OPERATORS.CLICK` },
    { value: 'impression', labelKey: `${KEY}.OPERATORS.IMPRESSION` },
];

const RECEIPT_VALIDATION_OPTIONS = [
    { value: 'not_valid', labelKey: `${KEY}.OPERATORS.NOT_VALID` },
    { value: 'valid', labelKey: `${KEY}.OPERATORS.VALID` },
];

const INTEGRATION_TYPE_OPTIONS = [
    { value: 's2s', labelKey: `${KEY}.OPERATORS.S2S` },
    { value: 'sdk', labelKey: `${KEY}.OPERATORS.SDK` },
];

const NUMBER_VALIDATION_REGEX = /^[-+]?[0-9]+(\.[0-9]+)?$/;

export function validateNumber(num) {
    return NUMBER_VALIDATION_REGEX.test(num);
}

export function validateText(text) {
    return Boolean(text) && typeof text === 'string' && text.trim() !== '';
}

export const validateCustomFieldName = customFieldName => !!customFieldName && customFieldName.trim() !== '';

export const validateCustomValues = values => values && values.length > 0;

function versionValidator(condition) {
    const versionForm = /^\d+(\.\d+)*(-rc\d|-beta|-alpha|-dev)?$/i;
    return condition.values.match(versionForm);
}

function customFieldValidator(condition) {
    return validateCustomFieldName(condition.customFieldName) && validateCustomValues(condition.values);
}

const DIM = `${KEY}.DIMENSIONS`;
const UNITS = `${KEY}.UNITS`;

function getVersionCondition({ value, labelKey, placeholder }) {
    return {
        value,
        labelKey,
        operators: ORDER_OPERATORS_AND_MISSING_VALUE,
        options: false,
        validate: versionValidator,
        validationTextKey: `${KEY}.VERSION_INVALID`,
        text: true,
        multi: false,
        placeholder,
        visible: true,
    };
}

export const CONDITION_TYPES = [
    {
        value: 'source',
        labelKey: `${DIM}.SOURCE`,
        operators: IN_NOT_IN,
        optionsKey: 'sources',
        multi: true,
        parseInt: true,
        visible: true,
    },
    { value: 'app', labelKey: `${DIM}.APP`, operators: IN_NOT_IN, optionsKey: 'apps', multi: true, visible: true },
    {
        value: 'publisher',
        labelKey: `${DIM}.PUBLISHER`,
        operators: IN_NOT_IN,
        multi: true,
        creatable: true,
        visible: true,
    },
    {
        value: 'time_to_install',
        labelKey: `${DIM}.TIME_TO_INSTALL`,
        operators: LESS_THAN_GREATER_THAN,
        options: false,
        number: true,
        visible: true,
        units: op => (op === GREATER_THAN_OPERATOR.value ? `${UNITS}.DAYS` : `${UNITS}.SECONDS`),
    },
    {
        value: 'dupes',
        labelKey: `${DIM}.DUPES`,
        operators: LESS_THAN_GREATER_THAN,
        options: false,
        number: true,
        visible: true,
    },
    {
        value: 'click_country',
        labelKey: `${DIM}.CLICK_COUNTRY`,
        operators: IN_NOT_IN,
        multi: true,
        optionsKey: 'countries',
        visible: true,
    },
    {
        value: 'install_country',
        labelKey: `${DIM}.INSTALL_COUNTRY`,
        operators: IN_NOT_IN,
        multi: true,
        optionsKey: 'countries',
        visible: true,
    },
    {
        value: 'install_region',
        labelKey: `${DIM}.INSTALL_REGION`,
        operators: IN_NOT_IN,
        multi: true,
        optionsKey: 'regions',
        visible: true,
    },
    {
        value: 'install_city',
        labelKey: `${DIM}.INSTALL_CITY`,
        operators: IN_NOT_IN,
        multi: true,
        optionsKey: 'cities',
        visible: true,
    },
    {
        value: 'campaign_name',
        labelKey: `${DIM}.CAMPAIGN_NAME`,
        operators: IN_NOT_IN,
        multi: true,
        creatable: true,
        visible: true,
    },
    {
        value: 'attribution_method',
        labelKey: `${DIM}.ATTRIBUTION_METHOD`,
        operators: ATTRIBUTION_METHODS,
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'integration_type',
        labelKey: `${DIM}.INTEGRATION_TYPE`,
        operators: INTEGRATION_TYPE_OPTIONS,
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'receipt_validation',
        labelKey: `${DIM}.RECEIPT_VALIDATION`,
        operators: RECEIPT_VALIDATION_OPTIONS,
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'click_distance',
        labelKey: `${DIM}.CLICK_DISTANCE`,
        operators: LESS_THAN_GREATER_THAN,
        options: false,
        number: true,
        visible: true,
        units: `${UNITS}.KM`,
    },
    {
        value: 'click_speed',
        labelKey: `${DIM}.CLICK_SPEED`,
        operators: LESS_THAN_GREATER_THAN,
        options: false,
        number: true,
        visible: true,
        units: `${UNITS}.KMH`,
    },
    {
        value: 'installer_source',
        labelKey: `${DIM}.INSTALLER_SOURCE`,
        operators: IN_NOT_IN,
        optionsKey: 'installSourceBundles',
        creatable: true,
        multi: true,
        visible: true,
    },
    getVersionCondition({ value: 'app_version', labelKey: `${DIM}.APP_VERSION`, placeholder: '2.3.1-beta' }),
    getVersionCondition({ value: 'os_version', labelKey: `${DIM}.OS_VERSION`, placeholder: '7.0' }),
    getVersionCondition({ value: 'sdk_version', labelKey: `${DIM}.SDK_VERSION`, placeholder: '7.4.0' }),
    {
        value: 'os_type',
        labelKey: `${DIM}.OS_TYPE`,
        operators: OS_OPTIONS,
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'touchpoint_type',
        labelKey: `${DIM}.TOUCHPOINT_TYPE`,
        operators: TOUCHPOINT_TYPE_OPTIONS,
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'referral_click_injection',
        labelKey: `${DIM}.REFERRAL_CLICK_INJECTION`,
        operators: [],
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'organic_poaching',
        labelKey: `${DIM}.ORGANIC_POACHING`,
        operators: [],
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'android_install_validation',
        labelKey: `${DIM}.ANDROID_INSTALL_VALIDATION`,
        operators: RECEIPT_VALIDATION_OPTIONS,
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'click_to_click_time',
        labelKey: `${DIM}.CLICK_TO_CLICK_TIME`,
        operators: LESS_THAN_GREATER_THAN,
        options: false,
        number: true,
        visible: true,
        units: `${UNITS}.SECONDS`,
    },
    {
        value: 'blacklisted_ip',
        labelKey: `${DIM}.BLACKLISTED_IP`,
        operators: [],
        options: false,
        hasValues: false,
        visible: true,
    },
    {
        value: 'custom_parameter',
        labelKey: `${DIM}.CUSTOM_PARAMETER`,
        operators: [...LESS_THAN_GREATER_THAN, ...IN_NOT_IN],
        options: false,
        validate: customFieldValidator,
        visible: true,
    },
];

export const ACTION_VALUES = {
    REJECT: 0,
    MARK_SUSPICIOUS: 1,
    WHITELIST: 2,
};

export const ACTIONS = {
    [ACTION_VALUES.REJECT]: `${KEY}.ACTIONS.REJECT`,
    [ACTION_VALUES.MARK_SUSPICIOUS]: `${KEY}.ACTIONS.MARK_SUSPICIOUS`,
    [ACTION_VALUES.WHITELIST]: `${KEY}.ACTIONS.WHITELIST`,
};

export const ACTION_DROPDOWN_OPTIONS = [
    { value: ACTION_VALUES.MARK_SUSPICIOUS, labelKey: `${KEY}.ACTIONS.MARK_SUSPICIOUS` },
    { value: ACTION_VALUES.REJECT, labelKey: `${KEY}.ACTIONS.REJECT` },
    { value: ACTION_VALUES.WHITELIST, labelKey: `${KEY}.ACTIONS.WHITELIST` },
];

export const DEFAULT_CONDITION = {
    dimension: 'source',
    operator: 'in',
    values: [],
};

export const DEFAULT_RULE = {
    name: 'New Rule',
    action: ACTION_VALUES.MARK_SUSPICIOUS,
    conditions: [DEFAULT_CONDITION],
    id: -1,
    active: true,
    dirty: true,
};

export const NEW_RULE_ID = -1;
export const MAX_RULE_NAME_LENGTH = 63;

// `sortKey` is the row field the comparator reads; `sortType` selects the comparator.
export const COLUMNS = [
    { key: 'name', labelKey: `${KEY}.COLUMNS.RULE`, width: 'auto', sortKey: 'name', sortType: 'string' },
    {
        key: 'updatedBy',
        labelKey: `${KEY}.COLUMNS.UPDATED_BY`,
        width: '200px',
        sortKey: 'updated_by',
        sortType: 'string',
    },
    {
        key: 'lastUpdated',
        labelKey: `${KEY}.COLUMNS.LAST_UPDATED`,
        width: '120px',
        sortKey: 'updated',
        sortType: 'string',
    },
    { key: 'action', labelKey: `${KEY}.COLUMNS.ACTION`, width: '160px', sortKey: 'action', sortType: 'num' },
    { key: 'status', labelKey: `${KEY}.COLUMNS.STATUS`, width: '95px', sortKey: 'active', sortType: 'string' },
];
